import { Call } from "@/lib/vibrato-api"
import { settings } from "@/settings"
import { Modal, ModalBody, ModalHeader, ModalFooter } from "flowbite-react"
import { Code, NavArrowDown, NavArrowUp } from "iconoir-react"
import { useCallback, useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import LoadingFullPage from "../Loading"

// TranscriptMessageModal component to display JSON content
function TranscriptMessageModal({ isOpen, onClose, message }: { isOpen: boolean; onClose: () => void; message: any }) {
  const isSystemMessage = message?.inner_msg?.type === "system"

  return (
    <Modal show={isOpen} onClose={onClose} size="xl" className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        <div className="flex items-center">
          <Code className="w-5 h-5 mr-2" />
          <span>{isSystemMessage ? "System Message" : "Message JSON Data"}</span>
        </div>
      </ModalHeader>
      <ModalBody>
        {isSystemMessage ? (
          <div className="bg-gray-100 text-gray-800 p-4 rounded-md overflow-auto max-h-[60vh] border border-gray-300">
            <pre className="whitespace-pre-wrap break-words text-sm font-mono">{message.inner_msg.content}</pre>
          </div>
        ) : (
          <div className="bg-gray-900 text-gray-100 p-4 rounded-md overflow-auto max-h-[60vh]">
            <pre className="whitespace-pre-wrap break-words text-sm">{JSON.stringify(message, null, 2)}</pre>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md transition-colors"
        >
          Close
        </button>
      </ModalFooter>
    </Modal>
  )
}

function PublicNavigation() {
  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <nav className="h-[88px] flex flex-col items-center">
        <div className="grow w-full flex px-4 md:px-8 xl:px-36 justify-between items-center h-16">
          <div className="flex items-center">
            <Link to="https://www.getvibrato.com">
              <img src="/vibrato-logo.svg" alt="Vibrato Logo" className="h-6 sm:h-8" />
            </Link>
          </div>
          <div className="flex items-center space-x-4">
            <a
              href="https://app.getvibrato.com/signup"
              className="flex flex-wrap items-center px-2 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-400 hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Try Vibrato
            </a>
            <a
              href="https://chat.openai.com/g/g-BsChUhBjC-make-a-phone-call-by-vibrato"
              className="hidden md:inline-flex items-center px-2 py-2 border border-purple-600 rounded-md text-sm font-medium text-purple-600 hover:bg-purple-50"
            >
              Try our GPT
            </a>
            <a
              href="https://app.getvibrato.com/dashboard"
              className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Sign In
            </a>
          </div>
        </div>
      </nav>
    </header>
  )
}

export default function ViewPublicCall() {
  const { uuid } = useParams()
  const [call, setCall] = useState<Call | null>(null)
  const [error, setError] = useState<boolean>(false)
  const [isInternal, setIsInternal] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState<any>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCallInfoCollapsed, setIsCallInfoCollapsed] = useState(false)

  useEffect(() => {
    const fetchCall = async () => {
      try {
        // First try the original endpoint
        let response = await fetch(`${settings.VIBRATO_URL}/api/public_calls/${uuid}/`)

        // If the first endpoint fails, try the internal endpoint
        if (!response.ok) {
          response = await fetch(`${settings.VIBRATO_URL}/api/public_calls/internal/${uuid}/`)
          setIsInternal(true)
        }

        if (!response.ok) {
          setError(true)
          throw new Error("Call not found")
        }

        const data = await response.json()
        setCall(data)
      } catch (error) {
        console.error("Error fetching call:", error)
        setError(true)
      }
    }

    fetchCall()
  }, [uuid, setIsInternal])

  const formatCallDuration = (duration: number) => {
    const hours = Math.floor(duration / 3600)
    const minutes = Math.floor((duration % 3600) / 60).toLocaleString("en-US", {
      minimumIntegerDigits: 2,
      useGrouping: false
    })
    const seconds = Math.floor((duration % 3600) % 60).toLocaleString("en-US", {
      minimumIntegerDigits: 2,
      useGrouping: false
    })

    return hours === 0 ? `${minutes}:${seconds}` : `${hours}:${minutes}:${seconds}`
  }

  const cleanMessage = useCallback(
    (message: string) => {
      if (!message) return message
      if (isInternal) return message
      return message
        .replace(/<<pause>>/g, "")
        .replace(/<<speaker-\d+>>/g, "")
        .replace(/<<hangup>>/g, "")
        .replace(/<<nothing>>/g, "")
        .replace(/<<h>>/g, "")
        .replace(/<<a>>/g, "")
        .replace(/<<handoff>>/g, "")
        .replace(/DTMF:/g, "")
        .trim()
    },
    [isInternal]
  )

  const handleShowMessageData = (message: any) => {
    setSelectedMessage(message)
    setIsModalOpen(true)
  }

  if (error) {
    return (
      <div className="h-screen bg-gray-50 flex flex-col">
        <PublicNavigation />
        {/* Error State Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <div className="text-center max-w-lg">
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Call Not Found</h2>
            <p className="text-gray-600 mb-8">
              We couldn&apos;t find the call you&apos;re looking for. It may have been deleted or the link might be
              incorrect.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://app.getvibrato.com/dashboard"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md text-base font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Go to Dashboard
              </a>
              <a
                href="https://www.getvibrato.com"
                className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 rounded-md text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Visit Homepage
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!call) {
    return <LoadingFullPage />
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <PublicNavigation />
      <div className="flex-none">
        <div className="bg-white shadow-lg relative">
          {/* Header and Summary */}
          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              isCallInfoCollapsed
                ? "max-h-0 opacity-0 py-0"
                : call.summary
                  ? "max-h-[500px] opacity-100"
                  : "max-h-[200px] opacity-100"
            }`}
          >
            {/* Header */}
            <div className="px-4 md:px-8 xl:px-36 py-8 border-b border-gray-200">
              <div className="flex flex-col flex-wrap gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Call Recording</h1>
                  <p className="mt-1 text-sm text-gray-500">
                    {new Date(call.created_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </p>
                  <div className="text-sm text-gray-500">Duration: {formatCallDuration(call.duration)}</div>
                </div>
                <div className="grow w-full flex items-center space-x-4 justify-center">
                  {call.recording_url && (
                    <div className="grow flex items-center justify-center">
                      <audio
                        controls
                        preload="metadata"
                        className="w-full max-w-md h-12"
                        style={{
                          filter: "sepia(20%) saturate(70%) hue-rotate(190deg) brightness(90%)"
                        }}
                      >
                        <source src={call.recording_url} type="audio/mpeg" />
                        <source src={call.recording_url} type="audio/wav" />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Call Summary */}
            {call.summary && (
              <div className="px-4 md:px-8 xl:px-36 py-6 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Summary</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{call.summary}</p>
              </div>
            )}
          </div>

          {/* Collapse/Expand Button - Fixed position when collapsed */}
          <div
            className={`${
              isCallInfoCollapsed
                ? "fixed top-[88px] right-4 md:right-8 xl:right-36 mt-2"
                : "absolute bottom-0 right-4 md:right-8 xl:right-36 transform translate-y-1/2"
            } z-50 transition-all duration-300 ease-in-out`}
          >
            <button
              onClick={() => setIsCallInfoCollapsed((prev) => !prev)}
              className="p-2 rounded-full bg-white shadow-md hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-200 border border-gray-100"
              aria-label={isCallInfoCollapsed ? "Expand call information" : "Collapse call information"}
            >
              {isCallInfoCollapsed ? (
                <NavArrowDown className="w-5 h-5 text-gray-600" />
              ) : (
                <NavArrowUp className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Transcript - Scrollable */}
      {call.transcript && (
        <div className={`flex-1 overflow-y-auto px-4 md:px-8 xl:px-36 py-6 ${isCallInfoCollapsed ? "pt-10" : ""}`}>
          <div className="w-full">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Transcript</h2>
            <div className="space-y-4">
              {call.transcript.messages.map((message: any, index: number) => (
                <div key={index} className="flex flex-col">
                  <div
                    className={`flex ${
                      message.inner_msg
                        ? message.inner_msg.type === "ai"
                          ? "justify-start"
                          : message.inner_msg.type === "system"
                            ? "justify-center"
                            : "justify-end"
                        : "justify-end" // If no inner_msg, assume it's human (justify-end)
                    }`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-2 ${
                        message.inner_msg
                          ? message.inner_msg.type === "ai"
                            ? "bg-gray-100 text-gray-900"
                            : message.inner_msg.type === "system"
                              ? "bg-gray-300 text-gray-700 border border-gray-400"
                              : "bg-blue-600 text-white"
                          : "bg-blue-200 bg-opacity-80 text-gray-800" // Light blue with opacity for Human Utterance
                      } ${isInternal ? "cursor-pointer hover:opacity-90 transition-opacity" : ""}`}
                      onClick={isInternal ? () => handleShowMessageData(message) : undefined}
                    >
                      <p className="text-sm font-medium mb-1 flex items-center justify-between">
                        <span>
                          {message.inner_msg
                            ? message.inner_msg.type === "ai"
                              ? "Vibrato AI"
                              : message.inner_msg.type === "system"
                                ? "System Message"
                                : "Human"
                            : "Utterance"}
                        </span>
                        {isInternal && <Code className="w-4 h-4 ml-2 opacity-70" />}
                      </p>
                      {(!message.inner_msg || message.inner_msg.type !== "system") && (
                        <p className="whitespace-pre-wrap">
                          {cleanMessage(
                            message.display_msg || (message.inner_msg ? message.inner_msg.content : message.text || "")
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal for displaying message JSON data */}
      {isInternal && selectedMessage && (
        <TranscriptMessageModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} message={selectedMessage} />
      )}
    </div>
  )
}
